import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Minus, Plus, Search, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { getImageUrl, API } from './adminUtils';
import RatingConfigSection from './RatingConfigSection';

const StatField = ({ label, testId, value, onChange, hidden }) => {
  // Local optimistic state - source of truth while user is clicking
  const [localValue, setLocalValue] = useState(Number(value) || 0);
  const debounceRef = useRef(null);
  const lastSyncedRef = useRef(Number(value) || 0);

  // Sync from prop ONLY when the upstream value actually changed (e.g., after backend fetch)
  // and we don't have a pending debounced commit.
  useEffect(() => {
    const v = Number(value) || 0;
    if (v !== lastSyncedRef.current && debounceRef.current === null) {
      setLocalValue(v);
      lastSyncedRef.current = v;
    }
  }, [value]);

  const commit = (newValue) => {
    setLocalValue(newValue);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      lastSyncedRef.current = newValue;
      debounceRef.current = null;
      onChange(newValue);
    }, 400);
  };

  const handleInputChange = (e) => {
    const newValue = parseInt(e.target.value) || 0;
    commit(Math.max(0, newValue));
  };

  const dec = () => commit(Math.max(0, localValue - 1));
  const inc = () => commit(localValue + 1);

  if (hidden) return null;

  return (
    <div>
      <label className="block text-sm font-bold mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={dec}
          className="w-9 h-10 flex items-center justify-center bg-red-100 hover:bg-red-200 border-2 border-red-500 text-red-700 font-bold rounded transition-colors"
          data-testid={`${testId}-minus`}
          aria-label={`Diminuir ${label}`}
        >
          <Minus size={16} strokeWidth={3} />
        </button>
        <input
          type="number"
          min="0"
          value={localValue}
          onChange={handleInputChange}
          className="input-field text-center flex-1 px-1"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={inc}
          className="w-9 h-10 flex items-center justify-center bg-green-100 hover:bg-green-200 border-2 border-green-500 text-green-700 font-bold rounded transition-colors"
          data-testid={`${testId}-plus`}
          aria-label={`Aumentar ${label}`}
        >
          <Plus size={16} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};

const StatsTab = ({ ctx }) => {
  const { players, handleUpdateStats, handleUpdatePlayerPosition, handleResetMonthlyStats, handleResetRankings } = ctx;
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({}); // playerName -> bool
  const [expandAll, setExpandAll] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(null); // null = loading, Set of keys otherwise
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/rating-config`).then(r => {
      if (!alive) return;
      const cfg = r.data?.config || [];
      setVisibleKeys(new Set(cfg.filter(c => c.enabled !== false).map(c => c.key)));
    }).catch(() => { if (alive) setVisibleKeys(new Set()); });
    return () => { alive = false; };
  }, []);

  const isOn = (key) => !visibleKeys || visibleKeys.has(key);

  const handleCleanupTitles = async () => {
    if (!window.confirm('Limpar títulos duplicados? Mantém apenas 1 título por (categoria + mês) por jogador. Não podes desfazer.')) return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API}/admin/cleanup-titles`);
      alert(`✅ ${r.data?.removed_total || 0} títulos removidos.`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao limpar títulos');
    }
    setCleaning(false);
  };

  const filtered = players.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const toggleAll = () => {
    if (expandAll) {
      setExpanded({});
      setExpandAll(false);
    } else {
      const all = {};
      filtered.forEach(p => { all[p.name] = true; });
      setExpanded(all);
      setExpandAll(true);
    }
  };

  const toggleOne = (name) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="card">
      <h2 className="text-3xl font-bold mb-6">ATUALIZAR ESTATÍSTICAS</h2>

      {/* Monthly Reset Button */}
      <div className="mb-4 p-6 border-2 border-yellow-500 bg-yellow-50">
        <h3 className="text-xl font-bold text-yellow-800 mb-2">🏆 ATRIBUIR PRÉMIOS DO MÊS</h3>
        <p className="text-base text-gray-700 mb-4">
          Fecha o mês corrente: atribui os títulos (Artilheiro, Garçom, MVP, Relógio de Ouro) aos top-players, acumula os totais na <strong>Central de Estatísticas</strong> e zera Gols/Assist/Faltas/Jogos.
          <br/><span className="text-sm text-gray-600">💡 Podes clicar em qualquer altura — o sistema é <strong>idempotente</strong>: se já tiver corrido para este mês, não duplica títulos.</span>
        </p>
        <button
          onClick={handleResetMonthlyStats}
          className="btn-secondary border-yellow-600 text-yellow-800 hover:bg-yellow-100"
          data-testid="reset-monthly-stats-btn"
        >
          🚀 ATRIBUIR PRÉMIOS &amp; FECHAR MÊS
        </button>
      </div>

      {/* Reset Rankings Button */}
      <div className="mb-8 p-6 border-2 border-red-500 bg-red-50">
        <h3 className="text-xl font-bold text-red-700 mb-4">⚠️ RESETAR RANKINGS</h3>
        <p className="text-base text-gray-700 mb-4">
          Usa esta opção para começar a votação do zero. Isto apaga TODOS os votos e rankings.
        </p>
        <button
          onClick={handleResetRankings}
          className="btn-secondary border-red-500 text-red-700 hover:bg-red-100"
          data-testid="reset-rankings-btn"
        >
          🗑️ RESETAR TODOS OS RANKINGS
        </button>
      </div>

      {/* Cleanup Titles */}
      <div className="mb-8 p-6 border-2 border-purple-500 bg-purple-50">
        <h3 className="text-xl font-bold text-purple-700 mb-2">🏆 LIMPAR TÍTULOS DUPLICADOS</h3>
        <p className="text-base text-gray-700 mb-4">
          Mantém apenas <strong>1 título por categoria por mês</strong> em cada jogador.
          Útil se o apuramento foi corrido várias vezes por engano.
        </p>
        <button
          onClick={handleCleanupTitles}
          disabled={cleaning}
          className="btn-secondary border-purple-500 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
          data-testid="cleanup-titles-btn"
        >
          <Trash2 className="inline mr-1" size={16} /> {cleaning ? 'A limpar…' : 'LIMPAR TÍTULOS DUPLICADOS'}
        </button>
      </div>

      {/* Rating Config */}
      <RatingConfigSection />

      {/* Search + expand-all toolbar */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center sticky top-0 bg-white z-10 py-2 -mx-2 px-2 border-b border-zinc-200">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar jogador…"
            className="w-full pl-10 pr-3 py-2 border-2 border-zinc-300 rounded-lg font-medium focus:border-zinc-900 outline-none"
            data-testid="stats-search-input"
          />
        </div>
        <button
          onClick={toggleAll}
          className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm rounded-lg whitespace-nowrap"
          data-testid="stats-toggle-all"
        >
          {expandAll ? '▲ Recolher todos' : '▼ Expandir todos'}
        </button>
        <span className="text-sm font-bold text-zinc-500 whitespace-nowrap">
          {filtered.length}/{players.length} jogador{filtered.length === 1 ? '' : 'es'}
        </span>
      </div>

      {/* Player Stats List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <p className="text-center py-8 text-zinc-400 font-bold">Nenhum jogador encontrado</p>
        )}
        {filtered.map((player) => {
          const isOpen = !!expanded[player.name];
          const position = player.position || 'linha';
          const totalShots = (player.goals || 0) + (player.shots_on_target || 0) + (player.shots_missed || 0);
          // Note: goals and shots_on_target are separate metrics - shot accuracy = on_target / (on_target + missed)
          const onTarget = (player.shots_on_target || 0);
          const offTarget = (player.shots_missed || 0);
          const shotAcc = onTarget + offTarget > 0 ? ((onTarget / (onTarget + offTarget)) * 100).toFixed(1) : '0';
          const passOk = (player.passes_completed || 0);
          const passFail = (player.passes_failed || 0);
          const passAcc = passOk + passFail > 0 ? ((passOk / (passOk + passFail)) * 100).toFixed(1) : '0';

          return (
            <div key={player.name} className="border-2 border-black rounded-lg overflow-hidden" data-testid={`player-card-${player.name}`}>
              {/* COLLAPSED HEADER (always visible, clickable) */}
              <button
                type="button"
                onClick={() => toggleOne(player.name)}
                className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50 transition-colors text-left"
                data-testid={`toggle-player-${player.name}`}
              >
                {player.profile_image_url ? (
                  <img
                    src={getImageUrl(player.profile_image_url)}
                    alt={player.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-black flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center border-2 border-black flex-shrink-0">
                    <span className="text-lg font-bold">{player.name.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-base sm:text-lg font-bold truncate">{player.name}</p>
                  <p className="text-xs text-zinc-500">
                    {position === 'linha' ? '⚽ Linha' : position === 'defesa' ? '🛡️ Defesa' : '🧤 Guarda-Redes'}
                    {' · '}
                    G {player.goals || 0} · A {player.assists || 0}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="bg-green-100 border border-green-400 px-3 py-1 rounded">
                    <span className="text-[10px] uppercase font-bold text-green-800 block leading-none">Pontos</span>
                    <span className="text-lg font-black text-green-900" data-testid={`pontos-${player.name}`}>
                      {(player.pontos_rating_monthly || 0).toFixed(1)}
                    </span>
                  </div>
                  {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>

              {/* EXPANDED BODY */}
              {isOpen && (
                <div className="p-4 border-t-2 border-zinc-200 bg-zinc-50/50">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs uppercase font-bold text-gray-500">Posição</span>
                    <select
                      value={position}
                      onChange={(e) => handleUpdatePlayerPosition(player.name, e.target.value)}
                      className="border-2 border-black px-2 py-1 font-bold text-sm bg-white"
                      data-testid={`position-select-${player.name}`}
                    >
                      <option value="linha">⚽ Linha</option>
                      <option value="defesa">🛡️ Defesa</option>
                      <option value="guarda_redes">🧤 Guarda-Redes</option>
                    </select>
                  </div>

              {/* ATAQUE */}
              <div className="mb-4">
                <h4 className="text-xs font-black uppercase text-green-700 mb-2 tracking-wider">⚽ Ataque</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="⚽ Golos" testId={`goals-input-${player.name}`} value={player.goals} onChange={(v) => handleUpdateStats(player.name, 'goals', v)} hidden={!isOn('goals')} />
                  <StatField label="🎯 Assist." testId={`assists-input-${player.name}`} value={player.assists} onChange={(v) => handleUpdateStats(player.name, 'assists', v)} hidden={!isOn('assists')} />
                  <StatField label="✅ Remates Baliza" testId={`shots-on-target-input-${player.name}`} value={player.shots_on_target} onChange={(v) => handleUpdateStats(player.name, 'shots_on_target', v)} hidden={!isOn('shots_on_target')} />
                  <StatField label="❌ Remates Fora" testId={`shots-missed-input-${player.name}`} value={player.shots_missed} onChange={(v) => handleUpdateStats(player.name, 'shots_missed', v)} hidden={!isOn('shots_missed')} />
                </div>
              </div>

              {/* DISTRIBUIÇÃO */}
              <div className="mb-4">
                <h4 className="text-xs font-black uppercase text-blue-700 mb-2 tracking-wider">📤 Distribuição</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="✅ Passes Certos" testId={`passes-completed-input-${player.name}`} value={player.passes_completed} onChange={(v) => handleUpdateStats(player.name, 'passes_completed', v)} hidden={!isOn('passes_completed')} />
                  <StatField label="❌ Passes Errados" testId={`passes-failed-input-${player.name}`} value={player.passes_failed} onChange={(v) => handleUpdateStats(player.name, 'passes_failed', v)} hidden={!isOn('passes_failed')} />
                </div>
              </div>

              {/* DEFESA */}
              <div className="mb-4">
                <h4 className="text-xs font-black uppercase text-purple-700 mb-2 tracking-wider">🛡️ Defesa</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="🦵 Desarmes" testId={`tackles-input-${player.name}`} value={player.tackles} onChange={(v) => handleUpdateStats(player.name, 'tackles', v)} hidden={!isOn('tackles')} />
                  <StatField label="🧱 Bloqueios" testId={`blocks-input-${player.name}`} value={player.blocks} onChange={(v) => handleUpdateStats(player.name, 'blocks', v)} hidden={!isOn('blocks')} />
                  {(position === 'defesa' || position === 'guarda_redes') && (
                    <StatField label="🛡️ JSG" testId={`clean-sheets-input-${player.name}`} value={player.clean_sheets} onChange={(v) => handleUpdateStats(player.name, 'clean_sheets', v)} hidden={!isOn('clean_sheets')} />
                  )}
                  {position === 'guarda_redes' && (
                    <StatField label="🧤 Defesas" testId={`saves-input-${player.name}`} value={player.saves} onChange={(v) => handleUpdateStats(player.name, 'saves', v)} hidden={!isOn('saves')} />
                  )}
                </div>
              </div>

              {/* RESULTADOS DA SESSÃO */}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded">
                <h4 className="text-xs font-black uppercase text-yellow-800 mb-2 tracking-wider">🏆 Resultados da Sessão</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="🏆 Vitórias" testId={`wins-input-${player.name}`} value={player.wins} onChange={(v) => handleUpdateStats(player.name, 'wins', v)} hidden={!isOn('wins')} />
                  <StatField label="🤝 Empates" testId={`draws-input-${player.name}`} value={player.draws} onChange={(v) => handleUpdateStats(player.name, 'draws', v)} hidden={!isOn('draws')} />
                  <StatField label="🕐 Jogos" testId={`games-played-input-${player.name}`} value={player.games_played} onChange={(v) => handleUpdateStats(player.name, 'games_played', v)} />
                </div>
              </div>

              {/* ERROS / FALTAS */}
              <div className="mb-4">
                <h4 className="text-xs font-black uppercase text-red-700 mb-2 tracking-wider">⚠️ Erros &amp; Faltas</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="🟨 Faltas" testId={`fouls-committed-input-${player.name}`} value={player.fouls_committed} onChange={(v) => handleUpdateStats(player.name, 'fouls_committed', v)} hidden={!isOn('fouls_committed')} />
                  <StatField label="💢 Sofridas" testId={`fouls-suffered-input-${player.name}`} value={player.fouls_suffered} onChange={(v) => handleUpdateStats(player.name, 'fouls_suffered', v)} hidden={!isOn('fouls_suffered')} />
                  <StatField label="🎯❌ Golo Contra" testId={`own-goals-input-${player.name}`} value={player.own_goals} onChange={(v) => handleUpdateStats(player.name, 'own_goals', v)} hidden={!isOn('own_goals')} />
                  <StatField label="🔄 Perda Posse" testId={`possession-lost-input-${player.name}`} value={player.possession_lost} onChange={(v) => handleUpdateStats(player.name, 'possession_lost', v)} />
                  <StatField label="❌ Erro Domínio" testId={`control-errors-input-${player.name}`} value={player.control_errors} onChange={(v) => handleUpdateStats(player.name, 'control_errors', v)} />
                  {position === 'guarda_redes' && (
                    <StatField label="🥅 Golo Sofrido" testId={`goals-conceded-input-${player.name}`} value={player.goals_conceded} onChange={(v) => handleUpdateStats(player.name, 'goals_conceded', v)} />
                  )}
                </div>
              </div>

              {/* EXTRAS - Drible/Interceção/Lançamento */}
              <div className="mb-4">
                <h4 className="text-xs font-black uppercase text-indigo-700 mb-2 tracking-wider">✨ Outras Ações</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <StatField label="💫 Fintas" testId={`dribbles-input-${player.name}`} value={player.dribbles} onChange={(v) => handleUpdateStats(player.name, 'dribbles', v)} />
                  <StatField label="🧤 Interceções" testId={`interceptions-input-${player.name}`} value={player.interceptions} onChange={(v) => handleUpdateStats(player.name, 'interceptions', v)} />
                  <StatField label="🚀 Lançamentos" testId={`throws-input-${player.name}`} value={player.throws} onChange={(v) => handleUpdateStats(player.name, 'throws', v)} />
                </div>
              </div>

              {/* COMPUTED METRICS */}
              <div className="mt-4 pt-3 border-t border-gray-300 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-green-100 px-3 py-2 rounded border border-green-400">
                  <span className="text-xs uppercase font-bold text-green-800">Pontos</span>
                  <p className="text-xl font-black text-green-900">
                    {(player.pontos_rating_monthly || 0).toFixed(1)}
                  </p>
                </div>
                <div className="bg-blue-50 px-3 py-2 rounded border border-blue-300">
                  <span className="text-xs uppercase font-bold text-blue-700">Prec. Remate</span>
                  <p className="text-xl font-black">{shotAcc}%</p>
                </div>
                <div className="bg-blue-50 px-3 py-2 rounded border border-blue-300">
                  <span className="text-xs uppercase font-bold text-blue-700">Prec. Passe</span>
                  <p className="text-xl font-black">{passAcc}%</p>
                </div>
                <div className="bg-orange-50 px-3 py-2 rounded border border-orange-300">
                  <span className="text-xs uppercase font-bold text-orange-700">G + A</span>
                  <p className="text-xl font-black">{(player.goals || 0) + (player.assists || 0)}</p>
                </div>
              </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatsTab;
